import { useContext, useEffect, useRef, useState } from "react";
import { OidcIdentityContext } from "@rsmus/react-auth";
import DataGrid, { Column, SearchPanel, Paging, Pager, Toolbar, Item, Button as GridButton, FilterRow, HeaderFilter } from "devextreme-react/data-grid";
import Popup from "devextreme-react/popup";
import Form, { SimpleItem } from "devextreme-react/form";
import { RequiredRule } from "devextreme-react/validator";
import Button from "devextreme-react/button";
import CheckBox from "devextreme-react/check-box";
import { Alert, Button as MuiButton } from "@mui/material";
import { AppName, dataGridConfig, defaultPageSize } from "../../constants";
import { taxPortalClientApi } from "../../api-client-nswag/taxportal-client-runtime";
import { GetAllRolesDTO, SiteNotificationDto } from "../../api-client-nswag/taxportal-client";
import { convertUTCTimeToLocal } from "../../util/Utils";
import AssociationsEditor, { formatAssociations, parseAssociations } from "./AssociationsEditor";
import "../custompage.scss";
import { DialogBox } from "../CreateSites/DialogBox";

const SiteNotifications = () => {
    const { user } = useContext(OidcIdentityContext);
    const [notifications, setNotifications] = useState<SiteNotificationDto[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [validationError, setValidationError] = useState("");
    const [rolesData, setRolesData] = useState<GetAllRolesDTO[]>([]);
    const [selectedRoleValues, setSelectedRoleValues] = useState<string[]>([]);
    const [activationIds, setActivationIds] = useState<number[]>([]);
    const [deactivationIds, setDeactivationIds] = useState<number[]>([]);
    const [selectionError, setSelectionError] = useState("");
    const [isActivating, setIsActivating] = useState(false);
    const [isDeactivating, setIsDeactivating] = useState(false);
    const [editingId, setEditingId] = useState<number | undefined>(undefined);
    const [associations, setAssociations] = useState<string | undefined>(undefined);
    const notificationFormRef = useRef<any>(null);
    // DevExtreme Form mutates this object in place, so its reference must stay stable while the popup is open.
    const formDataRef = useRef<any>({ isActive: true, roles: [] });
    const siteId = sessionStorage.getItem("siteId");
    const roleOptions = rolesData.map((role) => ({
        ...role,
        label: role.displayName || role.name || role.roleId || "Unknown role",
    }));
    const getRoleValues = (roles?: string | string[]) => Array.isArray(roles)
        ? roles.map((role) => String(role).trim()).filter(Boolean)
        : roles?.split(",").map((role) => role.trim()).filter(Boolean) ?? [];
    const getRoleIds = (roles?: string | string[]) => getRoleValues(roles).map((roleValue) => {
        const role = roleOptions.find((option) =>
            option.roleId === roleValue || option.name === roleValue || option.displayName === roleValue
        );
        return role?.roleId || roleValue;
    });
    const getRoleLabels = (roles?: string | string[]) => getRoleValues(roles).map((roleValue) => {
        const normalizedValue = roleValue.toLowerCase();
        const role = roleOptions.find((option) =>
            [option.roleId, option.name, option.displayName]
                .some((value) => value?.toLowerCase() === normalizedValue)
        );
        return role?.label || roleValue;
    }).join(", ");
    const selectedRoleNames = selectedRoleValues.flatMap((roleValue) => {
        const normalizedValue = roleValue.toLowerCase();
        const role = roleOptions.find((option) =>
            [option.roleId, option.name, option.displayName]
                .some((value) => value?.toLowerCase() === normalizedValue)
        );
        return role ? [role.name, role.displayName].filter((name): name is string => Boolean(name)) : [roleValue];
    });
    const associationTypeOptions = selectedRoleNames.length === 0
        ? Object.values(AppName)
        : Array.from(new Set(selectedRoleNames.flatMap((name) => {
            const normalizedName = name.toLowerCase();
            if (normalizedName.includes("tess")) return [AppName.TP, AppName.CS];
            if (normalizedName.includes("cs")) return [AppName.CS];
            return [AppName.TP];
        })));
    const populateRoleHeaderFilter = (options: any) => {
        const assignedRoleIds = new Set(notifications.flatMap((notification) => getRoleIds(notification.roles)));
        options.dataSource = roleOptions
            .filter((role) => role.roleId && assignedRoleIds.has(role.roleId))
            .map((role) => ({
                text: role.label,
                value: ["roles", "contains", role.roleId],
            }));
    };
    const serializeRoles = (roles?: string | string[]) => getRoleIds(roles).join(",");
    const renderCreatedDate = (event: any) => {
        const raw = event?.value ?? event?.data?.created;        
        if (!raw) return <div />;
        return <div>{convertUTCTimeToLocal(String(raw))}</div>;
    };
    const renderLastModifiedDate = (event: any) => {
        const raw = event?.value ?? event?.data?.lastModified;
        if (!raw) return <div />;
        return <div>{convertUTCTimeToLocal(String(raw))}</div>;
    };


    useEffect(() => {
        const fetchNotifications = async () => {
            try {
                setIsLoading(true);
                const data = await taxPortalClientApi.siteNotification_GetAll(
                    siteId ? Number(siteId) : undefined,
                    user?.profile?.preferred_username
                );
                setNotifications(data ?? []);
            } catch (error) {
                console.error(error);
                setNotifications([]);
            } finally {
                setIsLoading(false);
            }
        };
        fetchNotifications();
    }, [siteId, user?.profile?.preferred_username]);

    useEffect(() => {
        const fetchRoles = async () => {
            try {
                const rolesData: GetAllRolesDTO[] = await taxPortalClientApi.taxPortal_GetAllRoles();
                console.log('Roles Data-' +JSON.stringify(rolesData)    );
                setRolesData(rolesData ?? []);
            } catch (error) {
                console.error(error);
                setRolesData([]);
            }
        };
        fetchRoles();
    }, []);

    useEffect(() => {
        setActivationIds([]);
        setDeactivationIds([]);
        setSelectionError("");
    }, [notifications]);

    const refreshNotifications = async () => {
        const data = await taxPortalClientApi.siteNotification_GetAll(
            siteId ? Number(siteId) : undefined,
            user?.profile?.preferred_username
        );
        setNotifications(data ?? []);
    };

    const openPopup = (notification?: SiteNotificationDto) => {
        setValidationError("");
        const roleValues = getRoleIds(notification?.roles);
        formDataRef.current = {
            name: notification?.name ?? "",
            notificationMessage: notification?.notificationMessage ?? "",
            notificationType: notification?.notificationType ?? "",
            background: notification?.background ?? "",
            roles: roleValues,
            startDate: notification?.startDate ?? null,
            endDate: notification?.endDate ?? null,
            isActive: notification?.isActive ?? true,
        };
        setSelectedRoleValues(roleValues);
        setAssociations(notification?.associations);
        setEditingId(notification?.id);
        setIsPopupOpen(true);
    };

    const saveNotification = async () => {
        const formData = formDataRef.current;
        setValidationError("");
        const validationResult = notificationFormRef.current?.instance?.validate();
        if (validationResult && !validationResult.isValid) return;

        const name = formData.name?.trim();
        if (!name) {
            setValidationError("Enter an alert name. Alert names must be unique.");
            return;
        }
        if (!formData.notificationMessage?.trim()) {
            setValidationError("Enter a notification message.");
            return;
        }
        const payload: SiteNotificationDto = {
            ...formData,
            name,
            roles: serializeRoles(formData.roles),
            associations,
            siteId: siteId ? Number(siteId) : undefined,
        };
        try {
            setIsSaving(true);
            const allNotifications = await taxPortalClientApi.siteNotification_GetAll(
                undefined,
                user?.profile?.preferred_username,
            );
            const duplicateName = (allNotifications ?? []).some((notification) =>
                (editingId === undefined || notification.id !== editingId) &&
                notification.name?.trim().toLowerCase() === name.toLowerCase()
            );
            if (duplicateName) {
                setValidationError("Alert names must be unique. An alert with this title already exists; enter a different title.");
                return;
            }

            if (payload.isActive === true && parseAssociations(payload.associations).AllSites) {
                const existingActiveAllSitesNotification = (allNotifications ?? []).some((notification) =>
                    notification.id !== editingId &&
                    notification.isActive === true &&
                    parseAssociations(notification.associations).AllSites
                );

                if (existingActiveAllSitesNotification) {
                    setValidationError(
                        "An active all-sites notification already exists. Deactivate it before creating another active all-sites notification.",
                    );
                    return;
                }
            }

            if (editingId) {
                await taxPortalClientApi.siteNotification_Update(editingId, {
                    ...payload,
                    id: editingId,
                    lastModifiedBy: user?.profile?.preferred_username,
                });
            } else {
                await taxPortalClientApi.siteNotification_Create({
                    ...payload,
                    createdBy: user?.profile?.preferred_username,
                });
            }
            setIsPopupOpen(false);
            setActivationIds([]);
            setDeactivationIds([]);
            await refreshNotifications();
        } catch (error) {
            console.error(error);
            setValidationError("Unable to validate or save the notification. Please try again.");
        } finally {
            setIsSaving(false);
        }
    };

    const setNotificationsActive = async (isActive: boolean) => {
        if (isActivating || isDeactivating) return;
        if (activationIds.length > 0 && deactivationIds.length > 0) {
            setSelectionError("Activate and Deactivate cannot be selected at the same time. Clear the current selections before choosing the other action.");
            return;
        }
        const selectedIds = isActive ? activationIds : deactivationIds;
        const notificationsToUpdate = notifications.filter((notification) =>
            notification.isActive === !isActive && notification.id !== undefined && selectedIds.includes(notification.id)
        );
        if (!notificationsToUpdate.length) return;

        try {
            setIsActivating(isActive);
            setIsDeactivating(!isActive);
            await Promise.all(notificationsToUpdate.map((notification) =>
                taxPortalClientApi.siteNotification_Update(notification.id!, {
                    ...notification,
                    id: notification.id,
                    isActive,
                    lastModifiedBy: user?.profile?.preferred_username,
                })
            ));
            setActivationIds([]);
            setDeactivationIds([]);
            await refreshNotifications();
        } catch (error) {
            console.error(error);
        } finally {
            setIsActivating(false);
            setIsDeactivating(false);
        }
    };

    const renderStatusCheckbox = (notification: SiteNotificationDto, isActive: boolean) => {
        const selectedIds = isActive ? activationIds : deactivationIds;
        const setSelectedIds = isActive ? setActivationIds : setDeactivationIds;
        const notificationId = notification.id;
        const label = `${isActive ? "Activate" : "Deactivate"} ${notification.name ?? "alert"}`;

        return (
            <CheckBox
                value={notificationId !== undefined && selectedIds.includes(notificationId)}
                disabled={isActivating || isDeactivating || notificationId === undefined || notification.isActive !== !isActive}
                hint={label}
                elementAttr={{ "aria-label": label }}
                onValueChanged={(event) => {
                    if (!event.event) return;
                    if (notificationId === undefined || isActivating || isDeactivating || notification.isActive !== !isActive) return;
                    const oppositeIds = isActive ? deactivationIds : activationIds;
                    if (event.value === true && oppositeIds.length > 0) {
                        event.component.option("value", false);
                        setSelectionError("Activate and Deactivate cannot be selected at the same time. Clear the current selections before choosing the other action.");
                        return;
                    }
                    setSelectionError("");
                    setSelectedIds((current) => event.value === true
                        ? current.includes(notificationId) ? current : [...current, notificationId]
                        : current.filter((selectedId) => selectedId !== notificationId)
                    );
                }}
            />
        );
    };

    return (
        <div className="hq-page custom-page-shell m-20">
            <div className="custom-page-header">
                <h2 className="custom-page-title">Site Notifications</h2>
            </div>
            {selectionError && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setSelectionError("")}>{selectionError}</Alert>}
            <DataGrid
                id="site-notifications-grid"
                dataSource={notifications}
                noDataText={isLoading ? "Loading..." : "No notifications available"}
                keyExpr="id"

                {...dataGridConfig}
                selection={{ mode: "none" }}
            >
                <FilterRow visible={true} />
                <HeaderFilter visible={true} />
                <SearchPanel visible={true} width={260} placeholder="Search..." />
                <Paging defaultPageSize={defaultPageSize} />
                <Pager showPageSizeSelector={true} showInfo={true} />
                <Toolbar>
                    <Item location="after">
                        <MuiButton onClick={() => openPopup()} variant="outlined">
                            Add notification
                        </MuiButton>
                    </Item>
                    <Item location="after">
                        <MuiButton
                            onClick={() => setNotificationsActive(true)}
                            variant="outlined"
                            disabled={isActivating || isDeactivating || activationIds.length === 0 || deactivationIds.length > 0}
                        >
                            {isActivating ? "Activating..." : "Activate"}
                        </MuiButton>
                    </Item>
                    <Item location="after">
                        <MuiButton
                            onClick={() => setNotificationsActive(false)}
                            variant="outlined"
                            disabled={isActivating || isDeactivating || deactivationIds.length === 0 || activationIds.length > 0}
                        >
                            {isDeactivating ? "Deactivating..." : "Deactivate"}
                        </MuiButton>
                    </Item>

                    <Item name="searchPanel" location="after" />
                </Toolbar>
                <Column
                    name="activate"
                    caption="Activate"
                    alignment="center"
                    width={100}
                    allowSorting={false}
                    allowFiltering={false}
                    allowEditing={false}
                    cellRender={({ data }: { data: SiteNotificationDto }) => renderStatusCheckbox(data, true)}
                />
                <Column
                    name="deactivate"
                    caption="Deactivate"
                    alignment="center"
                    width={110}
                    allowSorting={false}
                    allowFiltering={false}
                    allowEditing={false}
                    cellRender={({ data }: { data: SiteNotificationDto }) => renderStatusCheckbox(data, false)}
                />
                <Column dataField="name" caption="Title" alignment="left" />
                <Column dataField="notificationMessage" caption="Message" alignment="left" />
                <Column
                    dataField="associations"
                    caption="Associations"
                    alignment="left" 
                    calculateDisplayValue={(row: SiteNotificationDto) => formatAssociations(row.associations)}
                    allowFiltering={false}

                />
                <Column
                    dataField="roles"
                    caption="Roles"
                    alignment="left"
                    calculateDisplayValue={(row: SiteNotificationDto) => getRoleIds(row.roles).map((roleId) =>
                        roleOptions.find((role) => role.roleId === roleId)?.label || roleId
                    ).join(", ")}
                    headerFilter={{ dataSource: populateRoleHeaderFilter }}
                />
               
                {/* <Column dataField="siteId" caption="Site ID" alignment="left" />
                <Column dataField="startDate" caption="Start Date" dataType="datetime" />
                <Column dataField="endDate" caption="End Date" dataType="datetime" /> 
                <Column dataField="background" caption="Background" alignment="left" />*/}
                <Column
                    dataField="created"
                    caption="Date Created"
                    dataType="datetime"
                    cellRender={renderCreatedDate}
                />
                <Column
                    dataField="lastModified"
                    caption="Last Modified"
                    alignment="left"
                    dataType="datetime"
                    cellRender={renderLastModifiedDate}
                />
                <Column type="buttons" caption="Actions" width={80}>
                    <GridButton hint="Edit" icon="edit" onClick={(event: any) => openPopup(event.row?.data)} />
                </Column>
            </DataGrid>
            <DialogBox
                isDisplayModal={isPopupOpen}
                handleClose={() => setIsPopupOpen(false)}
                handleContinue={saveNotification}
                title={editingId ? "Edit Site Notification" : "Add Site Notification"}
                btnOpenText={isSaving ? "Saving..." : "Save"}
                btnContinueDisable={isSaving}
                maxWidth="sm"
            >
                <div className="site-notification-dialog">
                    {validationError && <Alert severity="error" sx={{ mb: 2 }}>{validationError}</Alert>}
                    <Form
                        ref={notificationFormRef}
                        formData={formDataRef.current}
                        labelLocation="top"
                        showColonAfterLabel={false}
                        className="site-notification-form"
                    >
                        <SimpleItem
                            dataField="name"
                            label={{ text: "Title" }}
                            editorOptions={{
                                placeholder: "Enter a unique alert name",
                            }}
                        >
                            <RequiredRule message="Title is required." />
                        </SimpleItem>
                        <SimpleItem
                            dataField="notificationMessage"
                            label={{ text: "Message" }}
                            editorType="dxTextArea"
                            editorOptions={{
                                minHeight: 100,
                                maxHeight: 180,
                                autoResizeEnabled: true,
                                placeholder: "Enter notification message",
                            }}
                        >
                            <RequiredRule message="Message is required." />
                        </SimpleItem>

                        {/* <SimpleItem
                            dataField="background"
                            label={{ text: "Background" }}
                            editorOptions={{
                                placeholder: "Background",
                            }}
                        /> */}

                        <SimpleItem
                            dataField="roles"
                            label={{ text: "Roles" }}
                            editorType="dxTagBox"
                            editorOptions={{
                                dataSource: roleOptions,
                                valueExpr: "roleId",
                                displayExpr: "label",
                                showSelectionControls: true,
                                applyValueMode: "instantly",
                                searchEnabled: true,
                                placeholder: "Select roles",
                                onValueChanged: (event: { value?: string[] }) => setSelectedRoleValues(event.value ?? []),
                            }}
                        />

                        {/* <SimpleItem
                            dataField="isActive"
                            editorType="dxCheckBox"
                            editorOptions={{
                                text: "Active",
                            }}
                        /> */}
                    </Form>
                    <div className="association-title">
                        Association(S)
                    </div>
                    <AssociationsEditor
                        value={associations}
                        typeOptions={associationTypeOptions}
                        onChange={setAssociations}
                    />
                </div>
            </DialogBox>
        </div>
    );
};

export default SiteNotifications;
